/**
 * Scroll-driven steps (sections/scroll-steps.liquid).
 *
 * On desktop (no reduced motion) the section pins while the visitor scrolls through a tall track. Scroll
 * progress picks the active step: the photo crossfades, the timeline list slides so the active step sits
 * at the vertical center, steps before it turn "done" (gold line + dot) and the rest fade back.
 * Anywhere else the steps are left stacked (the CSS default), so nothing here runs.
 */
class ScrollSteps extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.stage = this.querySelector('[data-stage]');
    this.list = this.querySelector('[data-list]');
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.visuals = [...this.querySelectorAll('[data-visual]')];
    if (!this.track || this.steps.length < 2) return;

    this.desktop = window.matchMedia('(min-width: 1024px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMode = () => this.setMode();
    this.desktop.addEventListener('change', this.onMode);
    this.reduced.addEventListener('change', this.onMode);

    this.onScroll = () => {
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      }
    };
    this.onResize = () => {
      this.measure();
      this.update();
    };
    this.setMode();
  }

  disconnectedCallback() {
    this.desktop?.removeEventListener('change', this.onMode);
    this.reduced?.removeEventListener('change', this.onMode);
    this.disable();
  }

  setMode() {
    if (this.desktop.matches && !this.reduced.matches) this.enable();
    else this.disable();
  }

  enable() {
    if (this.classList.contains('is-pinned')) return;
    this.classList.add('is-pinned');
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    // Images and fonts change step heights, so measure again once everything has loaded.
    window.addEventListener('load', this.onResize, { once: true });
    requestAnimationFrame(() => {
      this.measure();
      this.update();
    });
  }

  disable() {
    this.classList.remove('is-pinned');
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.list) this.list.style.transform = '';
    this.steps.forEach((step) => step.classList.remove('is-done'));
    this.steps[0]?.classList.add('is-active');
    this.steps.slice(1).forEach((step) => step.classList.remove('is-active'));
  }

  /** Center line of each step within the list (px from the list's top). */
  measure() {
    // The sticky header slides back in when scrolling up. Tell the CSS how tall it is so the photo/video
    // frame is sized to stay clear of it (see --header-offset in sections/scroll-steps.liquid).
    const header = document.querySelector('.shopify-section:has(> .site-header[data-sticky])');
    if (header) {
      // Only the part that stays stuck counts: the announcement bar scrolls away.
      const announcement = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announcement-height')) || 0;
      this.style.setProperty('--header-offset', `${Math.round(header.getBoundingClientRect().height - announcement)}px`);
    }
    this.centers = this.steps.map((step) => step.offsetTop + step.offsetHeight / 2);
    this.stageHeight = this.list.parentElement.clientHeight;
  }

  update() {
    if (!this.centers) return;
    const rect = this.track.getBoundingClientRect();
    const scrollable = this.track.offsetHeight - window.innerHeight;
    const progress = scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 0;

    const position = progress * (this.steps.length - 1);
    const lower = Math.min(this.steps.length - 2, Math.floor(position));
    const mix = position - lower;
    const center = this.centers[lower] + (this.centers[lower + 1] - this.centers[lower]) * mix;
    this.list.style.transform = `translate3d(0, ${(this.stageHeight / 2 - center).toFixed(1)}px, 0)`;

    const active = Math.round(position);
    this.steps.forEach((step, i) => {
      step.classList.toggle('is-active', i === active);
      step.classList.toggle('is-done', i < active);
    });
    this.visuals.forEach((visual, i) => visual.classList.toggle('is-active', i === active));
  }
}
customElements.define('scroll-steps', ScrollSteps);
